import { useCallback } from 'react';
import type { Disposable, UseMutationConfig } from 'react-relay';
import { setIsViewConfigModifiedRecord } from '@jira/issue-navigator__saved-views-utils/src/setIsViewConfigModifiedRecord.tsx';
import type {
	main_updateFieldSetPreferencesMutation as UpdateFieldSetPreferencesMutation,
	main_updateFieldSetPreferencesMutation$data as UpdateFieldSetPreferencesMutationData,
	JiraIssueSearchSettings,
} from '@atlassian/jira-relay/src/__generated__/main_updateFieldSetPreferencesMutation.graphql';
import { MutationErrorCollection } from '@atlassian/jira-mutation-error/src/mutation-error-collection.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type {
	main_updateFieldSetPreferencesOldMutation as UpdateFieldSetPreferencesOldMutation,
	main_updateFieldSetPreferencesOldMutation$data as UpdateFieldSetPreferencesOldMutationData,
} from '@atlassian/jira-relay/src/__generated__/main_updateFieldSetPreferencesOldMutation.graphql';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../common/constants.tsx';

type FieldSetPreferenceUpdateType = 'isFrozen' | 'width';

type FieldSetPreferenceNode =
	| { fieldSetId: string; width: number | null }
	| { fieldSetId: string; isFrozen: boolean };

export const useCommitFieldSetPreferences = ({
	cloudId,
	projectId,
	viewId,
	isInlineFieldConfigEnabled,
	updateFieldSetPreferences,
	updateFieldSetPreferencesOld,
	settings,
	relayViewId,
}: {
	cloudId: string;
	viewId: string;
	projectId?: string | null;
	isInlineFieldConfigEnabled?: boolean;
	updateFieldSetPreferences: (
		config: UseMutationConfig<UpdateFieldSetPreferencesMutation>,
	) => Disposable;
	updateFieldSetPreferencesOld: (
		config: UseMutationConfig<UpdateFieldSetPreferencesOldMutation>,
	) => Disposable;
	settings: JiraIssueSearchSettings;
	relayViewId: string;
}) => {
	const commitUpdateFieldSetPreferencesOld = useCallback(
		(payload: FieldSetPreferenceNode[], updateType?: FieldSetPreferenceUpdateType) => {
			updateFieldSetPreferencesOld({
				variables: {
					cloudId,
					input: {
						nodes: payload,
					},
					...(isInlineFieldConfigEnabled && {
						context: {
							projectContext: {
								project: projectId,
							},
						},
						viewId,
					}),
				},
				onCompleted(data: UpdateFieldSetPreferencesOldMutationData) {
					if (!data.jira?.updateUserFieldSetPreferences?.success) {
						fireErrorAnalytics({
							meta: {
								id: 'commitUpdateFieldSetPreferencesMutationUnsuccessful',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							sendToPrivacyUnsafeSplunk: true,
							...(updateType ? { attributes: { updateType } } : {}),
						});
					}
				},
				onError(error: Error) {
					fireErrorAnalytics({
						meta: {
							id: 'commitUpdateFieldSetPreferencesMutationError',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						...(updateType ? { attributes: { updateType } } : {}),
					});
				},
			});
		},
		[cloudId, isInlineFieldConfigEnabled, projectId, updateFieldSetPreferencesOld, viewId],
	);

	const commitUpdateFieldSetPreferences = useCallback(
		(
			savedViewId: string,
			payload: FieldSetPreferenceNode[],
			updateType?: FieldSetPreferenceUpdateType,
		) => {
			updateFieldSetPreferences({
				variables: {
					cloudId,
					viewId: savedViewId,
					input: {
						nodes: payload,
					},
					settings,
				},
				/*
				 * This updater imperatively updates the isViewConfigModified record to handle the
				 * scenario where the variables provided to isViewConfigModified in the preloaded query
				 * differ from the variables provided in refetches.
				 *
				 * This ensures the save/reset view button is correctly hidden when publishing the view
				 * immediately after a page refresh.
				 *
				 * In all other scenarios we can rely on Relay writing the network response to the store
				 * via spreading a fragment which contains the isViewConfigModified field.
				 */
				updater: (store, response) => {
					const isViewConfigModified =
						response?.jira?.updateUserFieldSetPreferences?.view?.isViewConfigModified;
					if (
						response?.jira?.updateUserFieldSetPreferences?.success !== true ||
						isViewConfigModified == null
					) {
						return;
					}

					const viewRecord = store.get(relayViewId);
					if (viewRecord == null) {
						return;
					}

					setIsViewConfigModifiedRecord({
						isViewConfigModified,
						viewRecord,
					});
				},
				onCompleted(data: UpdateFieldSetPreferencesMutationData) {
					if (!data.jira?.updateUserFieldSetPreferences?.success) {
						const errors = new MutationErrorCollection(
							data.jira?.updateUserFieldSetPreferences?.errors,
						);
						// silently fail as column will remain at the desired width until the next page load
						fireErrorAnalytics({
							meta: {
								id: 'commitUpdateFieldSetPreferencesMutationUnsuccessful',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							...(updateType ? { attributes: { updateType } } : {}),
							error: errors,
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				},
				onError(error: Error) {
					// silently fail as column will remain at the desired width until the next page load
					fireErrorAnalytics({
						meta: {
							id: 'commitUpdateFieldSetPreferencesMutationError',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
						...(updateType ? { attributes: { updateType } } : {}),
					});
				},
			});
		},
		[cloudId, relayViewId, settings, updateFieldSetPreferences],
	);

	return {
		commitUpdateFieldSetPreferences,
		commitUpdateFieldSetPreferencesOld,
	};
};
